import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import type React from "react";
import { withRefProp } from "../../../utils/withRefProp";
import Icon from "../Icon/Icon";
import styles from "./Breadcrumb.module.scss";

export type BreadcrumbProps = React.ComponentPropsWithoutRef<"nav"> & {
  ref?: React.Ref<HTMLElement>;
};

const DefaultBreadcrumb = withRefProp(({ className, ref, ...props }: BreadcrumbProps) => {
  return (
    <nav
      ref={ref}
      aria-label={props["aria-label"] ?? "breadcrumb"}
      data-deci-breadcrumb="true"
      className={clsx(styles.breadcrumb, className)}
      {...props}
    />
  );
});

export type BreadcrumbListProps = React.ComponentPropsWithoutRef<"ol"> & {
  ref?: React.Ref<HTMLOListElement>;
};

const DefaultBreadcrumbList = withRefProp(({ className, ref, ...props }: BreadcrumbListProps) => (
  <ol
    ref={ref}
    data-deci-breadcrumb-list="true"
    className={clsx(styles.list, className)}
    {...props}
  />
));

export type BreadcrumbItemProps = React.ComponentPropsWithoutRef<"li"> & {
  ref?: React.Ref<HTMLLIElement>;
};

const DefaultBreadcrumbItem = withRefProp(({ className, ref, ...props }: BreadcrumbItemProps) => (
  <li
    ref={ref}
    data-deci-breadcrumb-item="true"
    className={clsx(styles.item, className)}
    {...props}
  />
));

export type BreadcrumbLinkProps = React.ComponentPropsWithoutRef<"a"> & {
  asChild?: boolean;
  ref?: React.Ref<HTMLAnchorElement>;
};

const DefaultBreadcrumbLink = withRefProp(({ asChild, className, ref, ...props }: BreadcrumbLinkProps) => {
  const Component = asChild ? Slot : "a";

  return (
    <Component
      ref={ref}
      data-deci-breadcrumb-link="true"
      className={clsx(styles.link, className)}
      {...props}
    />
  );
});

export type BreadcrumbPageProps = React.ComponentPropsWithoutRef<"span"> & {
  ref?: React.Ref<HTMLSpanElement>;
};

const DefaultBreadcrumbPage = withRefProp(({ className, ref, ...props }: BreadcrumbPageProps) => (
  <span
    ref={ref}
    data-deci-breadcrumb-page="true"
    aria-current="page"
    className={clsx(styles.page, className)}
    {...props}
  />
));

export type BreadcrumbSeparatorProps = React.ComponentPropsWithoutRef<"li"> & {
  ref?: React.Ref<HTMLLIElement>;
};

const DefaultBreadcrumbSeparator = withRefProp(({ children, className, ref, ...props }: BreadcrumbSeparatorProps) => (
  <li
    ref={ref}
    data-deci-breadcrumb-separator="true"
    role="presentation"
    aria-hidden="true"
    className={clsx(styles.separator, className)}
    {...props}
  >
    {children ?? <Icon name="go-forward" />}
  </li>
));

export type BreadcrumbEllipsisProps = React.ComponentPropsWithoutRef<"span"> & {
  ref?: React.Ref<HTMLSpanElement>;
};

const DefaultBreadcrumbEllipsis = withRefProp(({ className, ref, ...props }: BreadcrumbEllipsisProps) => (
  <span
    ref={ref}
    data-deci-breadcrumb-ellipsis="true"
    role="presentation"
    aria-hidden="true"
    className={clsx(styles.ellipsis, className)}
    {...props}
  >
    <Icon name="show-more" />
    <span className={styles.srOnly}>More</span>
  </span>
));

const BreadcrumbRoot = createPluggable<BreadcrumbProps>("deci.breadcrumb", DefaultBreadcrumb);
const BreadcrumbList = createPluggable<BreadcrumbListProps>("deci.breadcrumb.list", DefaultBreadcrumbList);
const BreadcrumbItem = createPluggable<BreadcrumbItemProps>("deci.breadcrumb.item", DefaultBreadcrumbItem);
const BreadcrumbLink = createPluggable<BreadcrumbLinkProps>("deci.breadcrumb.link", DefaultBreadcrumbLink);
const BreadcrumbPage = createPluggable<BreadcrumbPageProps>("deci.breadcrumb.page", DefaultBreadcrumbPage);
const BreadcrumbSeparator = createPluggable<BreadcrumbSeparatorProps>(
  "deci.breadcrumb.separator",
  DefaultBreadcrumbSeparator,
);
const BreadcrumbEllipsis = createPluggable<BreadcrumbEllipsisProps>(
  "deci.breadcrumb.ellipsis",
  DefaultBreadcrumbEllipsis,
);

export default Object.assign(BreadcrumbRoot, {
  List: BreadcrumbList,
  Item: BreadcrumbItem,
  Link: BreadcrumbLink,
  Page: BreadcrumbPage,
  Separator: BreadcrumbSeparator,
  Ellipsis: BreadcrumbEllipsis,
});
